import { isAxiosError } from "axios";

/**
 * 資料アップロードを同時実行する際の、1バッチあたりの上限件数。
 */
export const MAP_PLOT_REQUEST_DETAIL_UPLOAD_BATCH_SIZE = 10;

/**
 * 資料アップロードで許可する拡張子一覧（小文字）。
 */
export const MAP_PLOT_REQUEST_DETAIL_ALLOWED_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "xls",
  "xlsx",
  "csv",
]);

const MESSAGE_PRIORITY_KEYS = ["detail", "details", "nonFieldErrors", "file"];

/**
 * ファイル名から拡張子を抽出して小文字で返す。
 * 拡張子がない場合は空文字を返す。
 */
const getFileExtension = (fileName: string): string => {
  const parts = fileName.split(".");
  if (parts.length <= 1) {
    return "";
  }

  return parts.at(-1)?.toLowerCase() ?? "";
};

/**
 * APIエラーオブジェクト/配列/文字列から、表示用の先頭メッセージを抽出する。
 */
const getFirstMessage = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const message = getFirstMessage(item);
      if (message) {
        return message;
      }
    }
    return null;
  }

  if (typeof value === "object" && value !== null) {
    const record = value as Record<string, unknown>;

    for (const key of MESSAGE_PRIORITY_KEYS) {
      if (key in record) {
        const message = getFirstMessage(record[key]);
        if (message) {
          return message;
        }
      }
    }

    for (const key of Object.keys(record)) {
      const message = getFirstMessage(record[key]);
      if (message) {
        return message;
      }
    }
  }

  return null;
};

/**
 * 配列を指定件数ごとのバッチに分割する。
 * `batchSize <= 0` の場合は1バッチとして扱う。
 */
export const splitIntoBatches = <T>(items: T[], batchSize: number): T[][] => {
  if (items.length === 0) {
    return [];
  }

  if (batchSize <= 0) {
    return [items];
  }

  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize));
  }

  return batches;
};

/**
 * ファイル名の拡張子が資料アップロードの許可対象か判定する。
 */
export const isSupportedMapPlotRequestDetailFile = (
  fileName: string
): boolean => {
  const extension = getFileExtension(fileName);
  if (!extension) {
    return false;
  }

  return MAP_PLOT_REQUEST_DETAIL_ALLOWED_EXTENSIONS.has(extension);
};

export const isPreviewableMapPlotRequestDetailFileContentType = (
  contentType: string
): boolean => {
  const normalizedContentType = contentType.toLowerCase();

  return (
    normalizedContentType.startsWith("image/") ||
    normalizedContentType === "application/pdf"
  );
};

/**
 * Axiosエラーや一般的なErrorから、ユーザー表示用メッセージを生成する。
 */
export const extractMapPlotRequestDetailFileErrorMessage = (
  error: unknown,
  fallbackMessage: string
): string => {
  if (isAxiosError(error)) {
    const apiMessage = getFirstMessage(error.response?.data);
    if (apiMessage) {
      return apiMessage;
    }
  }

  if (error instanceof Error && !isAxiosError(error)) {
    const message = error.message.trim();
    if (message.length > 0) {
      return message;
    }
  }

  const genericMessage = getFirstMessage(error);
  return genericMessage ?? fallbackMessage;
};

/**
 * バイト数を画面表示用のサイズ文字列（B, KB, MB, ...）に変換する。
 */
export const formatMapPlotRequestDetailFileSize = (
  fileSize: number
): string => {
  if (!Number.isFinite(fileSize) || fileSize < 0) {
    return "-";
  }

  if (fileSize < 1024) {
    return `${Math.floor(fileSize)} B`;
  }

  const units = ["KB", "MB", "GB", "TB"];
  let size = fileSize / 1024;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  const fractionDigits = size >= 100 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(fractionDigits)} ${units[unitIndex]}`;
};
